import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useData } from '@bai/data';
import {
  AngularServiceNames,
  getAngularService,
} from '@bai/features/angular-integration';
import {
  CookieStore,
  refreshCookieStore,
  setUserRole,
} from '@bai/features/user';
import { Auth, RoleNames, TimesInMilliseconds } from '@bai/utils/types';

import {
  handleLoginFeatureSubmit,
  LoginFeatureResponse,
} from './loginFeatureSubmitHandler';
import { LoginPage } from './LoginPage';
import { UserThemeResponse } from './types';

// Lazy-load the zxcvbn password-strength checker and its large dictionaries
// (~6.5 MB unminified) only when they are first needed — after a successful
// login API response.  All three chunks share the same webpackChunkName so
// webpack emits them as a single async bundle that is NOT part of the initial
// login page download.
type ZxcvbnFn = typeof import('@zxcvbn-ts/core').zxcvbn;
let zxcvbnLoader: Promise<ZxcvbnFn> | null = null;

function loadZxcvbn(): Promise<ZxcvbnFn> {
  if (!zxcvbnLoader) {
    zxcvbnLoader = Promise.all([
      import(/* webpackChunkName: "password-strength" */ '@zxcvbn-ts/core'),
      import(
        /* webpackChunkName: "password-strength" */ '@zxcvbn-ts/language-common'
      ),
      import(
        /* webpackChunkName: "password-strength" */ '@zxcvbn-ts/language-en'
      ),
    ]).then(
      ([{ zxcvbn, zxcvbnOptions }, zxcvbnCommonPackage, zxcvbnEnPackage]) => {
        zxcvbnOptions.setOptions({
          translations: zxcvbnEnPackage.translations,
          graphs: zxcvbnCommonPackage.adjacencyGraphs,
          dictionary: {
            ...zxcvbnCommonPackage.dictionary,
            ...zxcvbnEnPackage.dictionary,
          },
        });
        return zxcvbn;
      },
    );
  }
  return zxcvbnLoader;
}

export function LoginPageData() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const portal = params.get('portal') ?? '';
  const endpoint = 'api/account/login';

  // if the user has a valid user cookie, send to the home page
  const cookies = CookieStore;

  useEffect(() => {
    if (cookies.get('user') !== undefined) {
      void navigate('/student/home/', { replace: true });
    }
  }, [cookies, navigate]);

  const [loggingIn, setLoggingIn] = useState(false);
  const [resetPasswordRequired, setPasswordResetRequired] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [weakPassword, setWeakPassword] = useState(false);

  const { data: customThemeResponse, isLoading } = useData<UserThemeResponse>(
    `account/theme/company/${portal ?? ''}`,
    {
      enabled: !!portal,
      staleTime: TimesInMilliseconds.ONE_HOUR,
    },
  );

  useEffect(() => {
    const fiveSeconds = 5 * 1000;
    if (loginError) {
      setTimeout(() => setLoginError(''), fiveSeconds);
    }
  }, [loginError]);

  const loginMethod = async (
    event: FormEvent,
    username: string,
    password: string,
    handleValidation: () => void,
  ) => {
    event.preventDefault();
    handleValidation();
    if (username === '' || password === '') {
      return;
    }

    setLoggingIn(true);
    setPasswordResetRequired(false);

    // Kick off zxcvbn loading in parallel with the login API call so the
    // chunk is likely ready before we need to evaluate password strength.
    const zxcvbnPromise = loadZxcvbn();

    const result = (await handleLoginFeatureSubmit(
      event,
      endpoint,
      password,
      username,
    )) as LoginFeatureResponse;

    if (result.ErrorMessage) {
      setLoginError(result.ErrorMessage);
    } else {
      const loginResult = result.data as Auth;

      refreshCookieStore();

      /* Update Angular's UserService state */
      const userService = getAngularService(AngularServiceNames.USER_SERVICE);
      if (!userService) {
        setLoginError(
          "An unknown error has occurred. Please contact your organization's training administrator.",
        );
        return setLoggingIn(false);
      }

      if (loginResult.loginStatus === 'successful') {
        userService.updateFromReact?.(loginResult);

        await setUserRole(RoleNames.Student);
        refreshCookieStore();
        /* check the password strength */
        const zxcvbnFn = await zxcvbnPromise;
        const passwordStrength = zxcvbnFn(password);
        if (passwordStrength.score >= 3) {
          void navigate('/student/home/', { replace: true });
        } else {
          // send to the new password page and show the message stating they have a weak password
          setWeakPassword(true);
        }
      } else if (loginResult.loginStatus.toLowerCase() === 'resetpassword') {
        userService.updateFromReact?.(loginResult);
        setPasswordResetRequired(true);
      }
    }
    setLoggingIn(false);
  };

  return (
    <LoginPage
      portal={portal}
      themeIsLoading={!portal || isLoading}
      customTheme={customThemeResponse}
      handleLogin={loginMethod}
      loggingIn={loggingIn}
      resetPasswordRequired={resetPasswordRequired}
      loginError={loginError}
      weakPassword={weakPassword}
    />
  );
}

export default LoginPageData;
